import { useState } from 'react';
import { X } from 'lucide-react';
import type { ParentRemovalSelectorProps } from './types';

export function ParentRemovalSelector({ isOpen, onClose, onRemove, question, allQuestions }: ParentRemovalSelectorProps) {
  const [selectedParents, setSelectedParents] = useState<string[]>([]);

  const currentParents = allQuestions.filter(q =>
    question.parent_ids && question.parent_ids.includes(q.id)
  );

  const handleToggleParent = (parentId: string) => {
    setSelectedParents(prev =>
      prev.includes(parentId) ? prev.filter(id => id !== parentId) : [...prev, parentId]
    );
  };

  const handleConfirm = () => {
    onRemove(selectedParents);
    setSelectedParents([]);
    onClose();
  };

  const handleCancel = () => {
    setSelectedParents([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[100] pt-12">
      <div className="bg-white  p-6 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            Remove Parent Relationships from: "{question.question}"
          </h3>
          <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto mb-4">
          {currentParents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              This question doesn't have any parent relationships to remove.
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Select which parent relationships you want to remove. The question will no longer appear as a follow-up to the selected parents.
              </p>
              {currentParents.map(parent => (
                <div
                  key={parent.id}
                  className={`p-3 border  cursor-pointer transition-colors ${selectedParents.includes(parent.id)
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => handleToggleParent(parent.id)}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedParents.includes(parent.id)}
                      className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 "
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{parent.question}</h4>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{parent.answer}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        {parent.is_root && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Root Question
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${parent.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                          {parent.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-gray-600">
            {selectedParents.length} parent relationship{selectedParents.length !== 1 ? 's' : ''} selected for removal
          </p>
          <div className="flex space-x-3">
            <button type="button" onClick={handleCancel} className="px-4 py-2 bg-gray-200 text-gray-800  hover:bg-gray-300">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedParents.length === 0}
              className="px-4 py-2 bg-red-600 text-white  hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Remove {selectedParents.length} Relationship{selectedParents.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
